import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { osLogin, osLogout } from "./onesignal";

const AuthCtx = createContext(null);

// Capture the Google OAuth session_id SYNCHRONOUSLY at module load — BEFORE React
// Router mounts and its "/" → default route redirect (<Navigate replace>) wipes the
// URL hash. Reading window.location.hash later inside an effect is TOO LATE because
// the child route's redirect effect runs before this provider's effect, so the
// #session_id fragment is already gone. Capturing here (at first bundle execution)
// runs before any render/redirect, so the id always survives.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
let CAPTURED_GOOGLE_SID = null;
try {
    const _m = (window.location.hash || "").match(/session_id=([^&]+)/);
    if (_m) {
        CAPTURED_GOOGLE_SID = decodeURIComponent(_m[1]);
        // strip the single-use id so a reload can't replay a consumed session
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
} catch { /* noop */ }

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);     // null = not authenticated
    const [loading, setLoading] = useState(true);
    const googleHandled = useRef(false);

    useEffect(() => {
        let active = true;
        (async () => {
            // Sign in with Google (Emergent OAuth): the session_id arrives in the URL
            // hash on return and is captured at module load (see CAPTURED_GOOGLE_SID
            // above) so a route redirect can't strip it before we read it. It is
            // SINGLE-USE, so guard with a ref (survives React StrictMode double-invoke).
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            if (CAPTURED_GOOGLE_SID) {
                if (googleHandled.current) { setLoading(false); return; }
                googleHandled.current = true;
                const sid = CAPTURED_GOOGLE_SID;
                CAPTURED_GOOGLE_SID = null;  // consume so a remount can't replay it
                try {
                    const { data } = await api.post("/auth/google/session", { session_id: sid });
                    localStorage.setItem("rosterly_token", data.token);
                    setUser(data.user);  // intentionally NOT gated by `active` — must survive StrictMode cleanup
                } catch (err) {
                    const status = err?.response?.status;
                    const detail = err?.response?.data?.detail;
                    if (status === 403) {
                        toast.error(typeof detail === "string" ? detail : "An invite code is required. Please use Sign up with your invite code.");
                    } else {
                        toast.error("Google sign-in failed or expired. Please try again.");
                    }
                    setUser(null);
                }
                setLoading(false);
                return;
            }
            // "Don't save this account": a QR sign-in only lasts until this browser is closed.
            if (localStorage.getItem("rosterly_nosave") === "1" && !sessionStorage.getItem("rosterly_alive")) {
                localStorage.removeItem("rosterly_token"); localStorage.removeItem("rosterly_nosave");
            }
            const token = localStorage.getItem("rosterly_token");
            if (!token) { if (active) { setUser(null); setLoading(false); } return; }
            try {
                const me = await api.get("/auth/me").then((r) => r.data);
                // Sliding session: the server hands back a token with a fresh
                // expiry on every load so active users are never logged out.
                if (me && me._token) { try { localStorage.setItem("rosterly_token", me._token); } catch { /* noop */ } delete me._token; }
                // Backfill Terms acceptance: if the user agreed on this device but
                // the server never recorded it (e.g. pre-existing/seeded accounts),
                // sync it once so their account reflects the acceptance.
                // NEVER auto-accept while the owner is impersonating another account —
                // that would wrongly mark the impersonated account as having agreed.
                const impersonating = !!localStorage.getItem("rosterly_owner_token");
                if (me && !me.tos_accepted_at && !impersonating && localStorage.getItem("rosterly_tos_agreed") === "yes") {
                    try {
                        const { data: t } = await api.post("/account/accept-terms");
                        me.tos_accepted_at = t?.tos_accepted_at || new Date().toISOString();
                    } catch { /* non-fatal */ }
                }
                if (active) setUser(me);
            } catch {
                localStorage.removeItem("rosterly_token");
                if (active) setUser(null);
            }
            if (active) setLoading(false);
        })();
        const onExpire = () => setUser(null);
        window.addEventListener("rosterly-auth-expired", onExpire);
        return () => { active = false; window.removeEventListener("rosterly-auth-expired", onExpire); };
    }, []);

    // Link this browser's OneSignal push subscription to the account (external_id).
    useEffect(() => {
        const uid = user?.id || user?._id || user?.email;
        // Never re-bind the device while impersonating / emulating another account —
        // the phone must keep receiving the OWNER's alerts only.
        if (uid && !localStorage.getItem("rosterly_owner_token")) osLogin(uid);
    }, [user?.id, user?._id, user?.email]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("rosterly_token", data.token);
        setUser(data.user);
    };

    const logout = () => {
        // Logging out of an owner impersonation / public test account returns to the owner.
        const ownerTok = localStorage.getItem("rosterly_owner_token");
        if (ownerTok) {
            localStorage.setItem("rosterly_token", ownerTok);
            localStorage.removeItem("rosterly_owner_token");
            window.location.href = "/";
            return;
        }
        const tk = localStorage.getItem("rosterly_token");
        if (tk) api.post("/account/sessions/revoke-current", null, { headers: { Authorization: `Bearer ${tk}` } }).catch(() => {});
        localStorage.removeItem("rosterly_token");
        localStorage.removeItem("rosterly_nosave");
        osLogout();
        setUser(null);
    };

    const refreshUser = async () => {
        try { const me = await api.get("/auth/me").then((r) => r.data); if (me && me._token) { try { localStorage.setItem("rosterly_token", me._token); } catch { /* noop */ } delete me._token; } setUser(me); return me; }
        catch { return null; }
    };

    const role = user?.role;
    const isOwner = role === "owner";    const isAdmin = role === "admin" || role === "owner";   // full content access
    const isEditor = role === "editor";
    const isBusiness = role === "business" || role === "owner";  // business-portal access
    const permissions = user?.permissions || [];
    const teamScope = user?.team_scope || {};
    const capabilities = user?.capabilities || [];
    // Can this account reach the admin area at all?
    const hasAdminAccess = isAdmin || (isEditor && permissions.length > 0);
    // View-mode grants (owner-managed per user).
    const canBusiness = isBusiness || capabilities.includes("business");
    const canCreate = isOwner || capabilities.includes("creation");
    // NHL is now fully public — available to every visitor (incl. logged-out).
    const canNhl = true;
    // Section-level permission check used to gate admin tabs.
    const can = (scope) => isAdmin || (isEditor && permissions.includes(scope));

    return (
        <AuthCtx.Provider value={{
            user, loading, login, logout, refreshUser,
            role, isOwner, isAdmin, isEditor, isBusiness,
            permissions, teamScope, capabilities, hasAdminAccess, canBusiness, canCreate, canNhl, can,
        }}>
            {children}
        </AuthCtx.Provider>
    );
};

export const useAuth = () => useContext(AuthCtx);
